import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import momentTimezone from "moment-timezone"
import { sortBy } from "lodash"
import type { TransactionsReportItem } from "./transactions/types"
import type { StateObject } from "../../redux/types"
import type { ReportsState, SummaryState, TimesheetState } from "./reducer"
import { getTransactionsQueryApiParam } from "./transactions/selectors"
import type { AgendaTypes } from "../../../utils/types/pagesTypes"
import { AgendaTypesEnum } from "../../../utils/types/pagesTypes"
import { getCustomersQueryApiParam } from "./customers/customersTable/selectors"
import type {
    NetCollectedBreakdownDataType,
    NetCollectedByEmployeeDataType,
    NetCollectedByPaymentTypeDataType,
    TransactionsByPaymentTypeDataType,
} from "./summary/types"
import { getFilters, getSelectedLocationIdsFromGlobalFilter } from "../../filters/selectors"
import { GLOBAL_FILTER_IDENTIFIER } from "../../filters/reducer"
import { T } from "../../../utils/queryApi/expressions"
import { getUserAccountById, getUserAccounts } from "../../data/selectors"
import type { UserAccount } from "../../data/types"
import { TimesheetRecord } from "../../data/types"
import { prepareQueryApiParameters } from "../../../utils/queryApi/queryApiRequest"
import { getCashReportQueryApiParam } from "./cash/selectors"
import { getMidnightOffset, getSettingByKeyAndLocationId } from "../settings/selectors"
import { getCustomersFeedbackQueryApiParamForDownload } from "./customers/customersFeedback/selectors"
import { composeFullName } from "../../../utils/general"
import { filterEmployeeBySelectedLocations } from "../employees/employees/selectors"
import { isLimitedAccessForDailySummary } from "./summary/selectors"
import { getSignedInUserAccount } from "../../auth/selectors"
import { getSalesByItemsReportV2QueryApiParam, getSalesByModifiersReportQueryApiParam } from "./salesByItems/selectors"
import { MultiSelectItem } from "../../uiKit/MultiSelect/MultiSelect"
import { arrayValuesDiffers } from "../../../utils/helpers"
import { getDateFromUtc, getDateString, getToday } from "../../../utils/dateFunctions"
import { TipsAndGratuityByAssignmentReportItem } from "../../../graphql/types"

export const getReportsStateObject = (state: StateObject): ReportsState => state.reports

export const getTimesheetReport = (state: StateObject): TimesheetState => getReportsStateObject(state).timesheet

export const getTransactionsReport = (state: StateObject): TransactionsReportItem[] =>
    getReportsStateObject(state).transactions

export const getSummaryReportState = (state: StateObject): SummaryState => getReportsStateObject(state).summary

export const getNetCollectedBreakdownData = (state: StateObject): NetCollectedBreakdownDataType =>
    getSummaryReportState(state).netCollectedBreakdownData

export const getNetCollectedBreakdownByPaymentTypeData = (state: StateObject): NetCollectedByPaymentTypeDataType =>
    getSummaryReportState(state).netCollectedByPaymentType

export const getTipsAndGratuityByAssignmentData = (state: StateObject): TipsAndGratuityByAssignmentReportItem[] =>
    getSummaryReportState(state).tipsAndGratuityByAssignmentData

export const getNetCollectedByEmployeeData = (state: StateObject): NetCollectedByEmployeeDataType[] =>
    getSummaryReportState(state).netCollectedByEmployeeData

export const getNetCollectedByEmployeeDetailData = (state: StateObject): any =>
    getSummaryReportState(state).netCollectedByEmployeeDetailData

export const getNetRevenueByAssignmentData = (state: StateObject): any =>
    getSummaryReportState(state).netRevenueByAssignmentData

export const getNetCollectedBySourceData = (state: StateObject): any =>
    getSummaryReportState(state).netCollectedBySourceData

export const getNetRevenueByCategoryData = (state: StateObject): any =>
    getSummaryReportState(state).netRevenueByCategoryData

export const getTransactionsByPaymentTypeData = (state: StateObject): TransactionsByPaymentTypeDataType[] =>
    getSummaryReportState(state).transactionsByPaymentTypeData

export const getSummarySelectedAssigneesIds = (state: StateObject): Maybe<string[]> => {
    const selectedIds = getSummaryReportState(state).selectedAssigneeIds
    const selectorAssignees = getSummaryAssigneesForSelector(state)
    return selectedIds ? selectedIds.filter(id => selectorAssignees.some(a => a.id === id)) : null
}

export const getSummarySelectedAssigneesIdsForHttpRequest = (state: StateObject): Maybe<string[]> => {
    if (isLimitedAccessForDailySummary(state)) {
        const signedInUserAccount = getSignedInUserAccount(state)

        if (!signedInUserAccount) {
            return []
        }

        return [signedInUserAccount.id]
    } else {
        const assignees = getSummaryAssigneesForSelector(state)
        const selectedIds = getSummarySelectedAssigneesIds(state) ?? []
        return selectedIds.length === 0 || assignees.length === selectedIds.length ? null : selectedIds
    }
}

export const isAddShiftModalOpened = (state: StateObject): Maybe<UserAccount> => {
    const editId = getTimesheetReport(state).addShiftModalUserAccountId

    if (editId) {
        return getUserAccountById(state, editId)
    }

    return null
}

export const getEditShiftUserAccountData = (state: StateObject): Maybe<UserAccount> => {
    const editData = getTimesheetReport(state).editShiftModalData

    if (editData) {
        return getUserAccountById(state, editData.userAccountId)
    }

    return null
}

export const getEditTimesheetModalData = (state: StateObject): Maybe<TimesheetRecord> =>
    getTimesheetReport(state).editShiftModalData

export const getDownloadReportQueryApiParam = (
    state: StateObject,
    reportAgenda: AgendaTypes,
    exportType: Maybe<"csv" | "excel">
): Maybe<Record<string, any>> => {
    if (reportAgenda === AgendaTypesEnum.transactionsReport) {
        return prepareQueryApiParameters(getTransactionsQueryApiParam(state))
    } else if (reportAgenda === AgendaTypesEnum.SummaryReport) {
        const globalFilters = getFilters(state, GLOBAL_FILTER_IDENTIFIER)
        const midnightOffset = getMidnightOffset(state)

        if (!globalFilters.timestamp) {
            return null
        }

        return {
            from: getDateFromUtc(globalFilters.timestamp.from, null, midnightOffset, "YYYY-MM-DD"),
            to: getDateFromUtc(globalFilters.timestamp.to, null, midnightOffset, "YYYY-MM-DD"),
            locations: getSelectedLocationIdsFromGlobalFilter(state),
            timezone: momentTimezone.tz.guess(),
            type: exportType,
        }
    } else if (reportAgenda === AgendaTypesEnum.Cash) {
        return getCashReportQueryApiParam(state)
    } else if (
        reportAgenda === AgendaTypesEnum.SalesByItems ||
        reportAgenda === AgendaTypesEnum.SalesByModifiers ||
        reportAgenda === AgendaTypesEnum.SalesByModifierGroups
    ) {
        return getSalesByItemsReportV2QueryApiParam(state)
    } else if (
        reportAgenda === AgendaTypesEnum.SalesByModifiers ||
        reportAgenda === AgendaTypesEnum.SalesByModifierGroups
    ) {
        return getSalesByModifiersReportQueryApiParam(state, true)
    } else if (reportAgenda === AgendaTypesEnum.Customers) {
        return prepareQueryApiParameters(getCustomersQueryApiParam(state))
    } else if (reportAgenda === AgendaTypesEnum.CustomersFeedback) {
        return getCustomersFeedbackQueryApiParamForDownload(state)
    } else if (reportAgenda === AgendaTypesEnum.Timesheet) {
        const selectedLocationIds = getSelectedLocationIdsFromGlobalFilter(state)

        const globalFilters = getFilters(state, GLOBAL_FILTER_IDENTIFIER)
        const filters = getFilters(state, "timesheet")
        let filter = globalFilters.timestamp
            ? T("clock_out_timestamp")
                  .ge(globalFilters.timestamp.from)
                  .or(T("clock_out_timestamp").isNull())
                  .and(T("clock_in_timestamp").le(globalFilters.timestamp.to))
            : T("timestamp").ge(0)
        filter =
            selectedLocationIds.length > 0
                ? filter.and(
                      T("clock_in_location_id")
                          .inRange(selectedLocationIds)
                          .or(T("clock_out_location_id").inRange(selectedLocationIds))
                  )
                : filter
        const offset = (filters.pagination.currentPage - 1) * filters.pagination.pageLimit
        return prepareQueryApiParameters({
            filter: filter,
            sort: [filters.sort],
            limit: filters.pagination.pageLimit,
            offset: offset,
        })
    }

    return null
}

export const DailySummaryUnassignedParameter: MultiSelectItem = {
    id: "unassigned",
    label: "Unassigned",
}

export const getSummaryAssigneesForSelector = (state: StateObject): MultiSelectItem[] => {
    const userAccounts = getUserAccounts(state)
    const result = sortBy(
        userAccounts
            .filter((u: UserAccount) => !u.deleted && u.isActive)
            .filter((u: UserAccount) => filterEmployeeBySelectedLocations(state, u))
            .map((u: UserAccount) => {
                return {
                    id: u.id,
                    label: composeFullName(u.firstName, u.surname),
                }
            }),
        "name"
    )
    return [...result, DailySummaryUnassignedParameter]
}

export const getReportDatapointTitle = (state: StateObject, key: string, defaultValue: string): string => {
    const selectedLocationIds = getSelectedLocationIdsFromGlobalFilter(state)

    const locationSettingsValues = selectedLocationIds
        ?.map(l => getSettingByKeyAndLocationId(state, key, l))
        ?.map(s => s?.value)

    if (arrayValuesDiffers(locationSettingsValues)) {
        return defaultValue
    }
    return locationSettingsValues?.[0] ?? defaultValue
}

export const getQueryFromDateOnly = (state: StateObject): string => {
    const today = getToday(state)
    const globalFilters = getFilters(state, GLOBAL_FILTER_IDENTIFIER)
    return globalFilters.date?.from ?? getDateString(today)
}

export const getQueryToDateOnly = (state: StateObject): string => {
    const today = getToday(state)
    const globalFilters = getFilters(state, GLOBAL_FILTER_IDENTIFIER)
    return globalFilters.date?.to ?? getDateString(today)
}
